import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from '@/components/ui';
import type { Vendor } from '../../../services/vendorService';
import { vendorService } from '../../../services/vendorService';
import { missingCallerServiceAreaIds } from '../utils/callerAreas';

interface CallerArea {
  id: number;
  name: string;
}

interface Props {
  show: boolean;
  vendor: Vendor | null;
  callerAreas: CallerArea[];
  onHide: () => void;
  onAdded: (vendor: Vendor) => void;
}

/**
 * Mark an existing vendor as available in the caller's service area(s). The server
 * refuses any id the caller does not hold, so the checkboxes here are already the
 * only legal set.
 */
const AddVendorToAreaModal: React.FC<Props> = ({ show, vendor, callerAreas, onHide, onAdded }) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missing = vendor ? missingCallerServiceAreaIds(vendor, callerAreas.map((a) => a.id)) : [];
  const options = callerAreas.filter((a) => missing.includes(a.id));

  useEffect(() => {
    if (!show || !vendor) return;
    setError(null);
    setSelected(missingCallerServiceAreaIds(vendor, callerAreas.map((a) => a.id)));
  }, [show, vendor, callerAreas]);

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor) return;
    if (selected.length === 0) {
      setError('Select at least one service area.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const updated = await vendorService.addVendorServiceAreas(vendor.id, selected);
      onAdded(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add vendor to your area');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Add to my area</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p className="tw-mb-3">
            <strong>{vendor?.name}</strong> is already set up in another service area. Adding it
            here lets you use it without creating a duplicate.
          </p>
          {options.length === 0 ? (
            <p className="tw-mb-0 tw-text-content-muted">
              This vendor is already available in every service area you can act in.
            </p>
          ) : (
            <Form.Group>
              <Form.Label>Service areas to add</Form.Label>
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                {options.map((sa) => (
                  <Form.Check
                    key={sa.id}
                    type="checkbox"
                    id={`add-vendor-sa-${sa.id}`}
                    label={sa.name}
                    checked={selected.includes(sa.id)}
                    onChange={() => toggle(sa.id)}
                  />
                ))}
              </div>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || options.length === 0}>
            {saving ? 'Adding…' : 'Add to my area'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddVendorToAreaModal;
